import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { useAppStore } from "../app/store";
import { invalidateEntity } from "../app/useLiveData";
import { keys } from "../lists/hooks";
import type { AgentActionCard, ChatMessage } from "../lists/types";
import { reviewKeys } from "../review/hooks";
import type { ReviewSessionState } from "../review/types";
import { wsClient, type ServerEvent, type WsStatus } from "../ws";

export type ChatItem =
  | {
      kind: "message";
      id: string;
      role: "user" | "assistant";
      text: string;
      at: string;
      /** Nazwa ceremonii, jeśli wiadomość zainicjował scheduler (Faza 4). */
      ceremony?: string | null;
    }
  | { kind: "action"; id: string; card: AgentActionCard; at: string }
  /** Karta podsumowania po `finish_review` (Faza 6). */
  | { kind: "review"; id: string; session: ReviewSessionState; at: string }
  /** Dyskretna notka systemowa (Faza 7: capture bez WS / offline). */
  | { kind: "note"; id: string; text: string; at: string }
  | { kind: "error"; id: string; text: string; at: string };

const HISTORY_PAGE = 40;

function itemFromMessage(message: ChatMessage): ChatItem | null {
  if (message.role !== "user" && message.role !== "assistant") return null;
  if (!message.content) return null; // assistant z samymi tool_calls - ślad audytu
  return {
    kind: "message",
    id: message.id,
    role: message.role,
    text: message.content,
    at: message.created_at,
    ceremony: message.ceremony,
  };
}

function byTime(a: ChatItem, b: ChatItem): number {
  return a.at === b.at ? 0 : a.at < b.at ? -1 : 1;
}

export function useWsStatus(): WsStatus {
  return useSyncExternalStore(wsClient.subscribeStatus, wsClient.getStatus, () => "closed");
}

export function useChat() {
  const qc = useQueryClient();
  const space = useAppStore((s) => s.space);
  const [items, setItems] = useState<ChatItem[]>([]);
  const [streaming, setStreaming] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const seen = useRef(new Set<string>());

  const addItems = useCallback((incoming: ChatItem[], position: "start" | "end") => {
    setItems((current) => {
      const fresh = incoming.filter((item) => !seen.current.has(item.id));
      for (const item of fresh) seen.current.add(item.id);
      if (fresh.length === 0) return current;
      const merged = position === "start" ? [...fresh, ...current] : [...current, ...fresh];
      return merged.sort(byTime);
    });
  }, []);

  const upsertAction = useCallback((card: AgentActionCard) => {
    setItems((current) => {
      const index = current.findIndex((item) => item.kind === "action" && item.id === card.id);
      if (index >= 0) {
        const next = [...current];
        next[index] = { ...(next[index] as Extract<ChatItem, { kind: "action" }>), card };
        return next;
      }
      seen.current.add(card.id);
      const at = card.created_at ?? new Date().toISOString();
      return [...current, { kind: "action" as const, id: card.id, card, at }].sort(byTime);
    });
  }, []);

  // --- historia -----------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [messages, actions] = await Promise.all([
          api.chatMessages({ limit: HISTORY_PAGE }),
          api.agentActions({ limit: 30 }),
        ]);
        if (cancelled) return;
        const fromMessages = messages.map(itemFromMessage).filter(Boolean) as ChatItem[];
        const fromActions: ChatItem[] = actions.map((card) => ({
          kind: "action" as const,
          id: card.id,
          card,
          at: card.created_at ?? new Date().toISOString(),
        }));
        setHasMore(messages.length >= HISTORY_PAGE);
        addItems([...fromMessages, ...fromActions], "end");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [addItems]);

  const loadOlder = useCallback(async () => {
    const oldest = items.find((item) => item.kind === "message");
    if (!oldest) return;
    const older = await api.chatMessages({ limit: HISTORY_PAGE, before: oldest.id });
    setHasMore(older.length >= HISTORY_PAGE);
    addItems(older.map(itemFromMessage).filter(Boolean) as ChatItem[], "start");
  }, [items, addItems]);

  // --- WebSocket ----------------------------------------------------------

  useEffect(() => {
    return wsClient.subscribe((event: ServerEvent) => {
      switch (event.type) {
        case "chat.message": {
          const phase = event.phase as string;
          if (phase === "start") setStreaming("");
          else if (phase === "delta") setStreaming((old) => (old ?? "") + String(event.text ?? ""));
          else if (phase === "final") {
            setStreaming(null);
            const text = String(event.text ?? "");
            if (text) {
              // Ceremonie przysyłają message_id - używamy go jako id pozycji,
              // żeby po przeładowaniu historii nie zdublować wiadomości.
              const id = event.message_id
                ? String(event.message_id)
                : `turn-${String(event.turn_id)}`;
              addItems(
                [
                  {
                    kind: "message",
                    id,
                    role: "assistant",
                    text,
                    at: new Date().toISOString(),
                    ceremony: (event.ceremony as string | undefined) ?? null,
                  },
                ],
                "end",
              );
            }
          }
          break;
        }
        case "agent.action":
          upsertAction(event as unknown as AgentActionCard);
          break;
        case "review.finished": {
          const session = event as unknown as ReviewSessionState;
          void qc.invalidateQueries({ queryKey: reviewKeys.current });
          addItems(
            [
              {
                kind: "review",
                id: `review-${session.id}`,
                session,
                at: new Date().toISOString(),
              },
            ],
            "end",
          );
          break;
        }
        case "data.changed":
          invalidateEntity(qc, String(event.entity));
          break;
        case "error":
          addItems(
            [
              {
                kind: "error",
                id: `err-${Date.now()}`,
                text: String(event.detail ?? "Nieznany błąd"),
                at: new Date().toISOString(),
              },
            ],
            "end",
          );
          setStreaming(null);
          break;
      }
    });
  }, [addItems, upsertAction, qc]);

  // Przełącznik przestrzeni w topbarze -> serwer zna aktywny filtr.
  useEffect(() => {
    wsClient.send({ type: "space.switch", space });
  }, [space]);

  // --- akcje użytkownika --------------------------------------------------

  const note = useCallback(
    (text: string) => {
      addItems(
        [{ kind: "note", id: `note-${Date.now()}`, text, at: new Date().toISOString() }],
        "end",
      );
    },
    [addItems],
  );

  /**
   * Wysyłka wiadomości.
   *
   * Normalnie idzie WebSocketem (streaming odpowiedzi agenta). Gdy socket jest
   * rozłączony - Faza 7 - nie gubimy myśli: wrzutka leci REST-em na /api/items
   * (a przy braku sieci ląduje w kolejce offline service workera). Agent
   * skomentuje ją, gdy połączenie wróci.
   */
  const send = useCallback(
    (text: string) => {
      const clean = text.trim();
      if (!clean) return;
      addItems(
        [
          {
            kind: "message",
            id: `local-${Date.now()}`,
            role: "user",
            text: clean,
            at: new Date().toISOString(),
          },
        ],
        "end",
      );

      if (wsClient.getStatus() === "open") {
        wsClient.send({ type: "chat.send", text: clean, space });
        return;
      }

      void (async () => {
        try {
          const result = await api.capture(clean, "chat");
          note(
            result.queued
              ? "Brak sieci — zapisane offline, wyślę do inboxu po jej odzyskaniu."
              : "Brak połączenia z agentem — wrzuciłem do inboxu. Odezwie się, gdy wróci sieć.",
          );
          if (!result.queued) void qc.invalidateQueries({ queryKey: [...keys.inbox] });
        } catch (error) {
          addItems(
            [
              {
                kind: "error",
                id: `err-${Date.now()}`,
                text:
                  error instanceof Error
                    ? `Nie udało się zapisać: ${error.message}`
                    : "Nie udało się zapisać wrzutki",
                at: new Date().toISOString(),
              },
            ],
            "end",
          );
        }
      })();
    },
    [addItems, note, qc, space],
  );

  const [busyAction, setBusyAction] = useState<string | null>(null);

  const runOnAction = useCallback(
    async (id: string, fn: (id: string) => Promise<AgentActionCard>) => {
      setBusyAction(id);
      try {
        upsertAction(await fn(id));
      } catch (error) {
        addItems(
          [
            {
              kind: "error",
              id: `err-${Date.now()}`,
              text: error instanceof Error ? error.message : "Nie udało się wykonać akcji",
              at: new Date().toISOString(),
            },
          ],
          "end",
        );
      } finally {
        setBusyAction(null);
      }
    },
    [upsertAction, addItems],
  );

  const confirm = useCallback((id: string) => runOnAction(id, api.confirmAction), [runOnAction]);
  const reject = useCallback((id: string) => runOnAction(id, api.rejectAction), [runOnAction]);
  const undo = useCallback((id: string) => runOnAction(id, api.undoAction), [runOnAction]);

  return {
    items,
    streaming,
    loading,
    hasMore,
    loadOlder,
    send,
    confirm,
    reject,
    undo,
    busyAction,
  };
}
